import { Icon } from '@/common/components/Icon'
import { Button, Modal } from '@/components/ui'
import { cn } from '@/lib/utils'

export type ValidationStatus =
  | 'INVALID_TICKET'
  | 'INVALID_TICKET_STATUS'
  | 'EXPIRED_TICKET'
  | 'INVALID_TRANSITION_STATUS'
  | 'ALREADY_CHECKED_IN'
  | 'CHECKED_IN'
  | 'ALREADY_CHECKED_OUT'
  | 'CHECKED_OUT'

export interface TicketHolder {
  name: string
  ticketCode: string
  email: string
  phone: string
  ticket: string
  status: ValidationStatus
}

interface ScanValidationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  status: ValidationStatus
  message?: string
  ticketHolder?: TicketHolder | null
}

const statusConfig: Record<
  ValidationStatus,
  { icon: 'check' | 'warning' | 'close'; color: string; label: string }
> = {
  CHECKED_IN: {
    icon: 'check',
    color: 'text-accent-1',
    label: 'Valid Check-In',
  },
  CHECKED_OUT: {
    icon: 'check',
    color: 'text-accent-1',
    label: 'Valid Check-Out',
  },
  ALREADY_CHECKED_IN: {
    icon: 'warning',
    color: 'text-accent-4',
    label: 'Already Scanned',
  },
  ALREADY_CHECKED_OUT: {
    icon: 'warning',
    color: 'text-accent-4',
    label: 'Already Scanned',
  },
  INVALID_TICKET: { icon: 'close', color: 'text-accent-3', label: 'Not Valid' },
  INVALID_TICKET_STATUS: {
    icon: 'close',
    color: 'text-accent-3',
    label: 'Not Valid',
  },
  EXPIRED_TICKET: { icon: 'close', color: 'text-accent-3', label: 'Not Valid' },
  INVALID_TRANSITION_STATUS: {
    icon: 'close',
    color: 'text-accent-3',
    label: 'Not Valid',
  },
}

const StatusIcon = ({ status }: { status: ValidationStatus }) => {
  const { icon, color } = statusConfig[status]
  return (
    <div
      className={cn(
        'w-[72px] h-[72px] rounded-full',
        'border-2 border-monochrome-120',
        'flex items-center justify-center',
        color,
      )}
    >
      {icon === 'close' ? (
        <Icon
          icon={icon}
          width="38"
          height="38"
          viewBox="0 0 24 24"
          fill="none"
        />
      ) : (
        <Icon
          icon={icon}
          width="38"
          height="38"
          viewBox="-3 -3 24 24"
          fill="none"
          color={icon === 'warning' ? '#FFCC00' : ''}
        />
      )}
    </div>
  )
}

const InfoField = ({ label, value }: { label: string; value: string }) => (
  <div className="border border-monochrome-120 rounded-xl px-4 py-3">
    <p className="text-monochrome-2 text-body-xs">{label}</p>
    <p className="text-accent-1 text-body-s">{value}</p>
  </div>
)

export const ScanValidationModal = ({
  open,
  onOpenChange,
  message,
  ticketHolder,
}: ScanValidationModalProps) => {
  const { label } = statusConfig[ticketHolder?.status || 'INVALID_TICKET']

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      contentClassName="p-0 border-2 border-monochrome-120"
      title={
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'h-[44px] w-[44px] rounded-full',
              'bg-monochrome-5 backdrop-blur-md',
              'border-2 border-monochrome-120',
              'flex items-center justify-center',
              'shadow-[0_12px_40px_rgba(0,0,0,0.45)]',
            )}
          >
            <div
              className={cn(
                'h-[40px] w-[40px] rounded-full',
                'flex items-center justify-center text-accent-1',
              )}
            >
              <Icon
                icon="ticket"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </div>
          </div>
          <h2 className="text-title-xxs text-monochrome-1 font-bold">
            Validation
          </h2>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-2 border-t border-monochrome-120 pt-6">
        <StatusIcon status={ticketHolder?.status || 'INVALID_TICKET'} />
        <h3
          className={cn(
            'text-title-xs font-bold mt-1',
            ticketHolder?.status === 'INVALID_TICKET'
              ? 'text-accent-3'
              : 'text-monochrome-1',
          )}
        >
          {label}
        </h3>
        {message && (
          <p className="text-monochrome-2 text-body-s text-center">{message}</p>
        )}
      </div>

      {ticketHolder?.ticket && (
        <div className="mt-6 flex flex-col gap-3 border-t border-monochrome-120 pt-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full border border-accent-1 flex items-center justify-center">
              <Icon
                icon="scan-ticket-profile"
                width="47"
                height="47"
                viewBox="7 1 33 47"
                fill="none"
                className="text-accent-1"
              />
            </div>
            <span className="text-monochrome-1 text-body-m font-medium">
              {ticketHolder.name}
            </span>
          </div>

          <InfoField label="Ticket Type" value={ticketHolder.ticket} />
          <InfoField label="Email" value={ticketHolder.email} />
          <InfoField label="Phone Number" value={ticketHolder.phone} />
        </div>
      )}

      <div className="flex w-full items-center justify-end mt-6 border-t border-monochrome-120 pt-4">
        <Button
          onClick={() => onOpenChange(false)}
          variant="primary"
          className="min-w-[154px]"
        >
          Done
        </Button>
      </div>
    </Modal>
  )
}
